"use client";

import Link from "@/components/LocaleLink";
import { ArrowRight } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "@/lib/i18n/LanguageContext";
import { FallingPetals } from "@/components/FallingPetals";
import { CtaLabel } from "@/components/CtaLabel";

type CtaLink = { href: string; label: ReactNode; track?: string };

type FinalCTAProps = {
  eyebrow?: ReactNode;
  headlineA?: ReactNode;
  /** The italic wine part of the heading. */
  headlineB?: ReactNode;
  lead?: ReactNode;
  primary?: CtaLink;
  /** The quiet text link under the red pill; `null` hides it. */
  secondary?: CtaLink | null;
  id?: string;
  className?: string;
  /** Falling petals behind the section; off on the guides (decided 18.10.2026). */
  petals?: boolean;
};

/**
 * Final CTA section: one primary action plus a quiet secondary path. The home
 * page uses the defaults; every other page ends with the same section and its
 * own words (27.09.2026), so no page ends in a dead end or in a dark band
 * directly above the dark footer.
 */
export function FinalCTA({
  eyebrow,
  headlineA,
  headlineB,
  lead,
  primary,
  secondary,
  id = "final-cta",
  className,
  petals = true,
}: FinalCTAProps = {}) {
  const { t } = useTranslation();
  const main = primary ?? {
    href: "/eignungs-check",
    label: t("finalCta", "option1Title"),
    track: "cta_final_eignungscheck",
  };
  const second =
    secondary === null
      ? null
      : (secondary ?? { href: "/preise", label: t("finalCta", "secondaryLink"), track: "cta_final_pricing" });

  return (
    <section
      id={id}
      className={"relative isolate overflow-hidden bg-cream-50 py-10 sm:py-14" + (className ? ` ${className}` : "")}
    >
      {petals ? <FallingPetals seed={10} /> : null}
      <div className="relative mx-auto max-w-2xl px-5 text-center sm:px-8 lg:px-10">
        <span className="eyebrow">{eyebrow ?? t("finalCta", "eyebrow")}</span>

        <h2
          data-reveal
          className="mt-3 text-balance font-display text-[1.5rem] font-normal leading-[1.12] text-ink-900 sm:text-[1.9rem]"
        >
          {headlineA ?? t("finalCta", "headlineA")}{" "}
          <span className="italic font-normal text-danish-700">{headlineB ?? t("finalCta", "headlineB")}</span>
        </h2>

        {lead !== null ? (
          <p data-reveal className="section-lead mx-auto mt-6">
            {lead ?? t("finalCta", "lead")}
          </p>
        ) : null}

        <div data-reveal className="mt-10 flex flex-col items-center gap-5">
          {/* data-hero-cta: the phone's sticky bar stays down while this red
              pill is on screen, so the same button never shows twice. */}
          <Link
            href={main.href}
            data-hero-cta
            data-track={main.track ?? "cta_final_primary"}
            className="focus-ring group inline-flex h-auto min-h-11 items-center justify-center gap-2 rounded-full bg-coral-500 px-7 py-2.5 text-center text-[14px] font-semibold leading-tight text-white transition hover:-translate-y-px hover:bg-coral-600 [&_svg]:shrink-0"
          >
            {main.href === "/eignungs-check" && typeof main.label === "string" ? (
              <CtaLabel long={main.label} short={t("dock", "checkNow")} />
            ) : (
              main.label
            )}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" strokeWidth={2} />
          </Link>

          {second ? (
            <Link
              href={second.href}
              data-track={second.track ?? "cta_final_secondary"}
              className="group -my-2 inline-flex items-center gap-1.5 py-2 text-[13.5px] font-semibold text-navy-700 transition-colors hover:text-coral-700"
            >
              {second.label}
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" strokeWidth={2} />
            </Link>
          ) : null}
        </div>
      </div>
    </section>
  );
}
